/**
 * Client de sauvegarde (GDD §13).
 *
 * Le localStorage seul est un piège pour un jeu qu'on reprend pendant des mois :
 * un nettoyage de navigateur et tout est perdu. On synchronise donc sur D1 dès
 * la v1, sans mur d'inscription : un UUID généré à la première visite suffit.
 * Le localStorage reste le cache local et le filet en cas de réseau coupé.
 */
import type { Etat } from "../sim/etat.ts"
import { uuid } from "./uuid.ts"

const CLE_ID = "dofusidle.compte"
const CLE_ETAT = "dofusidle.etat"
const CLE_VERSION = "dofusidle.version"

export function idCompte(): string {
  let id = localStorage.getItem(CLE_ID)
  if (!id) {
    id = uuid()
    localStorage.setItem(CLE_ID, id)
  }
  return id
}

export function lireLocal(): { etat: Etat; version: number } | null {
  const brut = localStorage.getItem(CLE_ETAT)
  if (!brut) return null
  try {
    return { etat: JSON.parse(brut), version: Number(localStorage.getItem(CLE_VERSION) ?? 0) }
  } catch {
    return null
  }
}

export function ecrireLocal(etat: Etat, version: number): void {
  localStorage.setItem(CLE_ETAT, JSON.stringify(etat))
  localStorage.setItem(CLE_VERSION, String(version))
}

export async function lireDistant(id: string): Promise<{ etat: Etat; version: number } | null> {
  const r = await fetch(`/api/save?id=${encodeURIComponent(id)}`)
  if (r.status === 404) return null          // ancien Worker
  if (!r.ok) throw new Error(`Lecture impossible (${r.status})`)
  const d = (await r.json()) as { save: Etat | null; version: number }
  return d.save ? { etat: d.save, version: d.version } : null
}

export type ResultatEcriture =
  | { ok: true; version: number }
  | { ok: false; conflit: { etat: Etat; version: number } }
  /** Un autre appareil tient le bail : cette partie ne doit plus avancer ici. */
  | { ok: false; verrou: { par: string | null; depuis: number } }

const CLE_APPAREIL = "dofusidle.appareil"

/**
 * L'identité de cet **appareil**, et non de cet onglet.
 *
 * Première version : un identifiant tiré à chaque chargement. C'était logique en
 * apparence — un rechargement est bien une nouvelle simulation — et catastrophique
 * en pratique : la session précédente tenait encore le bail, si bien que **rafraîchir
 * la page verrouillait le joueur hors de sa propre partie** et lui demandait de
 * cliquer « Reprendre ici » à chaque F5.
 *
 * L'identifiant vit donc dans le `localStorage`, à côté de la sauvegarde. Un
 * rechargement est transparent ; un autre navigateur ou un téléphone, lui, a
 * forcément le sien. Deux onglets du même navigateur restent indiscernables —
 * mais ils partagent déjà le même `localStorage`, donc la même partie.
 */
let appareil: string | null = null

/**
 * Calculé **à la demande**, jamais au chargement du module : `arbitrer` vit dans
 * ce fichier et se teste hors navigateur, où `localStorage` n'existe pas.
 */
export function idAppareil(): string {
  if (appareil) return appareil
  let id = localStorage.getItem(CLE_APPAREIL)
  if (!id) {
    id = uuid()
    localStorage.setItem(CLE_APPAREIL, id)
  }
  return (appareil = id)
}

/** Ce qu'on affiche à l'autre appareil : « Chrome sur Mac », « Safari sur iPhone ». */
export function nomAppareil(): string {
  const ua = navigator.userAgent
  const nav = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : "un navigateur"
  const sys = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac"
    : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "un appareil"
  return `${nav} sur ${sys}`
}

export async function ecrireDistant(id: string, etat: Etat, version: number): Promise<ResultatEcriture> {
  const r = await fetch("/api/save", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id, save: etat, version, session: idAppareil(), appareil: nomAppareil() }),
  })

  if (r.ok) {
    const d = (await r.json()) as { version: number }
    return { ok: true, version: d.version }
  }

  if (r.status === 409) {
    const d = (await r.json()) as { save: Etat; version: number }
    return { ok: false, conflit: { etat: d.save, version: d.version } }
  }

  if (r.status === 423) {
    const d = (await r.json()) as { verrou: string | null; depuis: number }
    return { ok: false, verrou: { par: d.verrou, depuis: d.depuis } }
  }

  throw new Error(`Écriture impossible (${r.status})`)
}

/** Prend le bail de force : c'est une décision explicite du joueur. */
export async function prendreLeBail(id: string): Promise<void> {
  await fetch("/api/session", {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify({ id, session: idAppareil(), appareil: nomAppareil(), action: "prendre" }),
  })
}

/**
 * Rend le bail en quittant la page.
 *
 * `sendBeacon` parce qu'un `fetch` lancé pendant `pagehide` n'a aucune garantie
 * d'aboutir : sans lui, fermer un onglet laissait le compte verrouillé jusqu'à
 * l'expiration, et l'appareil suivant devait attendre deux minutes.
 */
export function rendreLeBail(id: string): void {
  const corps = JSON.stringify({ id, session: idAppareil(), action: "rendre" })
  if (navigator.sendBeacon) {
    navigator.sendBeacon("/api/session", new Blob([corps], { type: "application/json" }))
  } else {
    void fetch("/api/session", { method: "POST", headers: { "content-type": "application/json" }, body: corps, keepalive: true })
  }
}

/**
 * Arbitrage en cas de conflit : deux appareils sur le même compte.
 *
 * Le plus avancé dans le temps de jeu gagne, et **il gagne en entier** : on ne
 * fusionne pas deux branches de simulation, ça n'aurait pas de sens. La branche
 * perdue est jetée, avec ce qu'elle contenait depuis la divergence.
 *
 * **Une seule valeur échappe à la règle : le curseur du marché.**
 *
 * Il ne décrit pas la progression du joueur mais ce que le serveur lui a déjà
 * remis (GDD §20). Le laisser reculer ferait rejouer des crédits déjà encaissés
 * — kamas et objets crédités deux fois — et le marché est justement la partie du
 * jeu où une erreur touche les autres joueurs. Il ne peut donc que monter, quelle
 * que soit la branche qui l'emporte.
 */
export function arbitrer(local: Etat, distant: Etat): Etat {
  const gagnant = distant.lastTick > local.lastTick ? distant : local
  const curseur = Math.max(local.hdvCurseur ?? 0, distant.hdvCurseur ?? 0)
  return curseur === (gagnant.hdvCurseur ?? 0) ? gagnant : { ...gagnant, hdvCurseur: curseur }
}
